import {
  HStack,
  Tag,
  Text,
  useColorModeValue,
  VStack,
} from '@cardboard-ui/react';
import { isPastEvent } from 'utils/event';
import { t } from '@lingui/macro';
import { WarningIcon } from 'components/icons';
import React, { FC, PropsWithChildren } from 'react';
import { graphql, useFragment } from 'utils/graphClient';
import { useI18n } from 'utils/i18n';
import { DateTimeLabel_event$key } from './__generated__/DateTimeLabel_event.graphql';

const eventCard_event = graphql`
  fragment DateTimeLabel_event on Event {
    id
    start {
      date
      time
      timestamp
      utcDate
      utcTime
      timezone
    }
    end {
      date
      time
      timestamp
      utcDate
      utcTime
      timezone
    }
    allDay
  }
`;

export const DateTimeLabel = ({
  event: _event,
}: {
  event: DateTimeLabel_event$key;
}) => {
  const event = useFragment(eventCard_event, _event);

  // Cast to string required due to `unknown` type def on graph schema
  const startDate = event.start?.date ? `${event.start?.date}` : undefined;
  const startTime = event.start?.time ? `${event.start?.time}` : undefined;
  const endDate = event.end?.date ? `${event.end?.date}` : undefined;
  const endTime = event.end?.time ? `${event.end?.time}` : undefined;

  return (
    <DateTimeLabelWrapper
      isOutlined={isPastEvent(!!startDate, endDate, endTime)}
    >
      <DateTimeLabelContent
        startDate={startDate}
        startTime={startTime}
        endDate={endDate}
        endTime={endTime}
        allDay={event.allDay}
      />
    </DateTimeLabelWrapper>
  );
};

interface DateTimeLabelContentProps {
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  allDay: boolean;
}

const DateTimeLabelContent = ({
  startDate,
  endDate,
  startTime,
  endTime,
  allDay,
}: DateTimeLabelContentProps) => {
  let content = <TBDText />;
  if (startDate && endDate) {
    if (startDate === endDate) {
      content = (
        <SingleDateTimeLabelContent
          startDate={startDate}
          startTime={startTime}
          endDate={endDate}
          endTime={endTime}
          allDay={allDay}
        />
      );
    } else {
      content = (
        <MultiDateTimeLabelContent
          startDate={startDate}
          startTime={startTime}
          endDate={endDate}
          endTime={endTime}
          allDay={allDay}
        />
      );
    }
  }
  return content;
};

const TBDText = () => (
  <Text as="span">
    {t`Time to be defined`}&nbsp;
    <Text as="span" color="yellow.300">
      <WarningIcon />
    </Text>
  </Text>
);

interface SingleDateTimeLabelContentProps extends DateTimeLabelContentProps {
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
}

const SingleDateTimeLabelContent = ({
  startDate,
  startTime,
  endDate,
  endTime,
  allDay,
}: SingleDateTimeLabelContentProps) => {
  const { format } = useI18n();
  const startDateD = new Date(startDate + (startTime ? `T${startTime}` : ''));
  const endDateD = new Date(endDate + (endTime ? `T${endTime}` : ''));

  let timeIndicatorText: string | JSX.Element = (
    <Text as="span">
      {'- '}
      <TBDText />
    </Text>
  );

  if (allDay) {
    timeIndicatorText = '- ' + t`All day`;
  } else if (startTime && endTime) {
    timeIndicatorText = `${format(startDateD, 'p')} - ${format(endDateD, 'p')}`;
  }

  return (
    <>
      <Text as="b">{`${format(new Date(startDateD), 'PP')}`}</Text>
      &nbsp;
      {timeIndicatorText}
    </>
  );
};

const MultiDateTimeLabelContent = ({
  startDate,
  startTime,
  endDate,
  endTime,
  allDay,
}: SingleDateTimeLabelContentProps) => {
  const { format } = useI18n();
  let firstDayText = <Text as="b">{format(new Date(startDate), 'PP')}</Text>;
  if (startTime) {
    firstDayText = (
      <Text>
        {firstDayText}
        <br />
        {format(new Date(startDate + 'T' + startTime), 'p')}
      </Text>
    );
  }

  let secondDayText = <Text as="b">{format(new Date(endDate), 'PP')}</Text>;
  if (endTime) {
    secondDayText = (
      <Text>
        {secondDayText}
        <br />
        {format(new Date(endDate + 'T' + endTime), 'p')}
      </Text>
    );
  }

  let warning: string | JSX.Element = '';
  if (!startTime || !endTime) {
    warning = (
      <Text>
        &nbsp;-&nbsp;
        <TBDText />
      </Text>
    );
  }

  return (
    <VStack>
      <HStack>
        {firstDayText}
        <span>-</span>
        {secondDayText}
      </HStack>
      {warning}
    </VStack>
  );
};

interface DateTimeLabelWrapperProps {
  isOutlined: boolean;
}

const DateTimeLabelWrapper: FC<
  PropsWithChildren<DateTimeLabelWrapperProps>
> = ({ isOutlined, children }) => {
  const bgColor = useColorModeValue('gray.600', 'gray.300');
  const textColor = useColorModeValue('white', 'gray.600');

  return (
    <Tag
      size="md"
      variant={isOutlined ? 'outline' : 'solid'}
      bgColor={isOutlined ? textColor : bgColor}
      color={isOutlined ? bgColor : textColor}
      py={1}
    >
      {children}
    </Tag>
  );
};
